import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddExternalNetworkDomainsDto,
  AddExternalNetworkNetblocksDto,
  DomainDto,
  DomainListQueryDto,
  NetBlockDto,
  NetBlockListQueryDto,
  Scope,
} from '@terra/shared-contracts-external-network';
import { externalNetworkScopeApi } from '../../clients/tester-client/external-network-scope-api';
import { EXTERNAL_NETWORK_STATS_KEY } from './useExternalNetworkStats';
import { SURFACE_ASSETS_KEY } from './useSurfaceAssets';
import { EXTERNAL_NETWORK_DETAIL_KEY } from './useSurfaceSettings';

export const EXTERNAL_NETWORK_SCOPE_NETBLOCKS_KEY =
  'external-network-netblocks';
export const EXTERNAL_NETWORK_SCOPE_DOMAINS_KEY = 'external-network-domains';

const EMPTY_NETBLOCKS: NetBlockDto[] = [];
const EMPTY_DOMAINS: DomainDto[] = [];

export function useExternalNetworkScopeNetblocks({
  surfaceId,
  query,
}: {
  surfaceId: string | null;
  query: NetBlockListQueryDto;
}) {
  const result = useQuery({
    queryKey: [EXTERNAL_NETWORK_SCOPE_NETBLOCKS_KEY, surfaceId, query],
    queryFn: () =>
      externalNetworkScopeApi.listScopeNetblocks({
        surfaceId: surfaceId ?? '',
        query,
      }),
    enabled: Boolean(surfaceId),
  });

  return {
    netblocks: result.data?.data ?? EMPTY_NETBLOCKS,
    total: result.data?.total ?? 0,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}

export function useExternalNetworkScopeDomains({
  surfaceId,
  query,
}: {
  surfaceId: string | null;
  query: DomainListQueryDto;
}) {
  const result = useQuery({
    queryKey: [EXTERNAL_NETWORK_SCOPE_DOMAINS_KEY, surfaceId, query],
    queryFn: () =>
      externalNetworkScopeApi.listScopeDomains({
        surfaceId: surfaceId ?? '',
        query,
      }),
    enabled: Boolean(surfaceId),
  });

  return {
    domains: result.data?.data ?? EMPTY_DOMAINS,
    total: result.data?.total ?? 0,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}

export function useUpdateExternalNetworkNetblockScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      surfaceId: string;
      netblockId: string;
      scope: Scope;
    }) => externalNetworkScopeApi.updateNetblockScope(variables),
    onSuccess: (_data, { surfaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_SCOPE_NETBLOCKS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_STATS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({ queryKey: [SURFACE_ASSETS_KEY] });
    },
  });
}

export function useUpdateExternalNetworkDomainScope() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      surfaceId: string;
      domainId: string;
      scope: Scope;
    }) => externalNetworkScopeApi.updateDomainScope(variables),
    onSuccess: (_data, { surfaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_SCOPE_DOMAINS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_STATS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({ queryKey: [SURFACE_ASSETS_KEY] });
    },
  });
}

export function useAddExternalNetworkScopeDomains() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      surfaceId: string;
      dto: AddExternalNetworkDomainsDto;
    }) => externalNetworkScopeApi.addScopeDomains(variables),
    onSuccess: (_data, { surfaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_SCOPE_DOMAINS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_DETAIL_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_STATS_KEY],
      });
      void queryClient.invalidateQueries({ queryKey: [SURFACE_ASSETS_KEY] });
    },
  });
}

export function useAddExternalNetworkScopeNetblocks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      surfaceId: string;
      dto: AddExternalNetworkNetblocksDto;
    }) => externalNetworkScopeApi.addScopeNetblocks(variables),
    onSuccess: (_data, { surfaceId }) => {
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_SCOPE_NETBLOCKS_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_DETAIL_KEY, surfaceId],
      });
      void queryClient.invalidateQueries({
        queryKey: [EXTERNAL_NETWORK_STATS_KEY],
      });
      void queryClient.invalidateQueries({ queryKey: [SURFACE_ASSETS_KEY] });
    },
  });
}
